import { getAgentHold, holdAgent, resumeAgent } from '../team-hold/teamHold';
import type { LanguageCode } from '../platform-languages';
import type {
  PlanPage,
  TcAgentSegment,
  TcCampaign,
  TcNumbers,
  TcOpportunity,
  TcRecentBooking,
} from './types';

/**
 * Treatment Coordinator demo — Alamo Heights Dental (amendment 27).
 *
 * `TC_NUMBERS` is the anchor every page reads (§1). The campaign store, the
 * agent page and the Opportunity funnel all reconcile to it: the by-campaign
 * table sums to 288 messaged / 164 opened / 47 booked / $96,400, and the agent
 * hero's three how-booked segments sum to $96,400.
 */
export const TC_NUMBERS: TcNumbers = {
  reachable: 312,
  reachableValue: '$284,000',
  booked30: 47,
  bookedValue: '$96,400',
  messaged30: 288,
  completedValue: '$61,200',
  acceptancePct: 16,
  acceptanceWas: 11,
  openedPlan: 164,
};

/* ---- campaigns ----------------------------------------------------------- */

const SEED: TcCampaign[] = [
  {
    id: 'freshly-diagnosed',
    name: 'Freshly diagnosed',
    description: 'Diagnosed in the last 30 days',
    state: 'active',
    inProgress: 61,
    messaged: 96,
    openedPlan: 68,
    steps: 4,
    channels: ['text', 'email'],
    booked30: 21,
    value30: '$38,200',
  },
  {
    id: 'benefits-ending',
    name: 'Benefits ending',
    description: '$300+ of benefits left, resets within 3 months',
    state: 'active',
    inProgress: 84,
    messaged: 88,
    openedPlan: 47,
    steps: 4,
    channels: ['text', 'email'],
    booked30: 12,
    value30: '$22,100',
  },
  {
    id: 'larger-cases',
    name: 'Larger cases',
    description: 'Cases of $1,500 or more, diagnosed in the last 6 months',
    state: 'active',
    inProgress: 38,
    messaged: 41,
    openedPlan: 29,
    steps: 5,
    channels: ['text', 'email'],
    booked30: 8,
    value30: '$29,400',
  },
  {
    id: 'second-try',
    name: 'Second try',
    description: 'Contacted 90+ days ago, never replied',
    state: 'active',
    inProgress: 102,
    messaged: 63,
    openedPlan: 20,
    steps: 3,
    channels: ['text', 'email'],
    booked30: 6,
    value30: '$6,700',
  },
  {
    id: 'crowns-saito',
    name: 'Crowns $1K–$2K · Dr. Saito',
    description: 'Crowns and onlays between $1,000 and $2,000 by Dr. Saito',
    state: 'draft',
    steps: 0,
    channels: ['text', 'email'],
  },
];

export const tcCampaignStore: { campaigns: TcCampaign[] } = { campaigns: SEED.map((c) => ({ ...c })) };

export function tcCampaignById(id: string): TcCampaign | undefined {
  return tcCampaignStore.campaigns.find((c) => c.id === id);
}

export function reorderTcCampaigns(from: number, to: number): void {
  const list = [...tcCampaignStore.campaigns];
  const [moved] = list.splice(from, 1);
  if (moved === undefined) return;
  list.splice(to, 0, moved);
  tcCampaignStore.campaigns = list;
}

export function setTcCampaignState(id: string, state: TcCampaign['state']): void {
  tcCampaignStore.campaigns = tcCampaignStore.campaigns.map((c) => (c.id === id ? { ...c, state } : c));
}

export function duplicateTcCampaign(id: string): void {
  const row = tcCampaignById(id);
  if (row === undefined) return;
  // Copy the steps too (fix 29 §3.2) — keep steps + channels; it's a draft, so it
  // isn't running: drop the in-progress and last-30 figures entirely.
  const { inProgress, booked30, value30, messaged, openedPlan, ...rest } = row;
  void inProgress; void booked30; void value30; void messaged; void openedPlan;
  tcCampaignStore.campaigns = [
    ...tcCampaignStore.campaigns,
    { ...rest, id: `${row.id}-copy-${String(Date.now())}`, name: `Copy of ${row.name}`, state: 'draft' },
  ];
}

export function deleteTcCampaign(id: string): void {
  tcCampaignStore.campaigns = tcCampaignStore.campaigns.filter((c) => c.id !== id);
}

/**
 * Add (or update) a draft from the builder's Save as draft (fix 29 §3.2 / §4.3):
 * a new campaign lands in the list as a Draft row; editing an existing one keeps
 * its place.
 */
export function saveTcDraft(draft: TcCampaignDraft): void {
  const channels = Array.from(new Set(draft.steps.map((s) => s.channel)));
  const existing = tcCampaignById(draft.id);
  if (existing !== undefined) {
    tcCampaignStore.campaigns = tcCampaignStore.campaigns.map((c) =>
      c.id === draft.id ? { ...c, name: draft.name || c.name, description: draft.description, steps: draft.steps.length, channels } : c,
    );
    return;
  }
  tcCampaignStore.campaigns = [
    ...tcCampaignStore.campaigns,
    { id: draft.id, name: draft.name || 'Untitled campaign', description: draft.description, state: 'draft', steps: draft.steps.length, channels },
  ];
}

/** The header line on the Campaigns tab (§4) — counted, not written. */
export function tcCampaignsHeader(): string {
  const active = tcCampaignStore.campaigns.filter((c) => c.state === 'active').length;
  return `${String(TC_NUMBERS.reachable)} reachable · ${String(tcCampaignStore.campaigns.length)} campaigns · ${String(active)} active · ${String(TC_NUMBERS.booked30)} booked in the last 30 days`;
}

/** Hold, exactly as Recall/Reminders/Gap Filler (§9 → amendment 24 §6). */
export const tcHoldStore = { get onHold(): boolean { return getAgentHold('treatment').held; } };
export function setTcHold(onHold: boolean): void {
  if (onHold) holdAgent('treatment', { by: 'Ava Thompson', since: '9:10 am', waiting: 12 });
  else resumeAgent('treatment');
}

/* ---- opportunity --------------------------------------------------------- */

export const TC_OPPORTUNITY: TcOpportunity = {
  funnel: [
    { key: 'gross', label: 'Diagnosed, not booked', patients: 2812, value: '$1,842,000', kind: 'gross' },
    {
      key: 'unreachable',
      label: 'Not seen in 18 months, or can’t be reached',
      note: 'Recall’s reactivation list',
      patients: 902,
      value: '−$586,000',
      kind: 'deduction',
    },
    {
      key: 'over-year',
      label: 'Diagnosed over a year ago',
      note: 'Recall’s reactivation list',
      patients: 1088,
      value: '−$664,000',
      kind: 'deduction',
    },
    { key: 'booked', label: 'Already booked', patients: 284, value: '−$142,000', kind: 'deduction' },
    {
      key: 'on-hold',
      label: 'On hold in the PMS',
      note: 'Marked on hold by the dentist',
      patients: 226,
      value: '−$166,000',
      kind: 'deduction',
    },
    { key: 'reachable', label: 'Reachable now', patients: 312, value: '$284,000', kind: 'total' },
  ],
  averageCase: '$910',
  diagnosedThisMonth: '$52,400',
  diagnosedThisMonthCases: 61,
  caseBands: [
    { key: 'u300', label: 'Under $300', patients: 96, value: '$19,400', bookedPct: '41%', campaignDescription: 'Patients with diagnosed treatment under $300' },
    { key: '300-800', label: '$300 – $800', patients: 88, value: '$46,200', bookedPct: '33%', campaignDescription: 'Patients with diagnosed treatment between $300 and $800' },
    { key: '800-1500', label: '$800 – $1,500', patients: 62, value: '$71,800', bookedPct: '22%', campaignDescription: 'Patients with diagnosed treatment between $800 and $1,500' },
    { key: '1500-3000', label: '$1,500 – $3,000', patients: 41, value: '$76,700', bookedPct: '14%', campaignDescription: 'Patients with diagnosed treatment between $1,500 and $3,000' },
    { key: 'over3000', label: 'Over $3,000', patients: 25, value: '$69,900', bookedPct: '8%', campaignDescription: 'Patients with diagnosed treatment over $3,000' },
  ],
  providers: [
    { key: 'saito', label: 'Dr. Saito', patients: 94, value: '$106,200', campaignDescription: 'Treatment diagnosed by Dr. Saito' },
    { key: 'chen', label: 'Dr. Chen', patients: 81, value: '$86,400', campaignDescription: 'Treatment diagnosed by Dr. Chen' },
    { key: 'okafor', label: 'Dr. Okafor', patients: 62, value: '$56,100', campaignDescription: 'Treatment diagnosed by Dr. Okafor' },
    { key: 'hygiene', label: 'Hygiene — perio', patients: 75, value: '$35,300', campaignDescription: 'Perio treatment diagnosed at hygiene' },
  ],
  procedures: [
    { key: 'crown', label: 'Crown & onlay', patients: 98, value: '$96,300', campaignDescription: 'Crowns and onlays' },
    { key: 'perio', label: 'Perio — scaling', patients: 71, value: '$53,100', campaignDescription: 'Perio scaling treatment' },
    { key: 'implant', label: 'Implant', patients: 14, value: '$48,400', campaignDescription: 'Implants' },
    { key: 'rootcanal', label: 'Root canal', patients: 29, value: '$40,700', campaignDescription: 'Root canals' },
    { key: 'fillings', label: 'Fillings', patients: 86, value: '$28,900', campaignDescription: 'Fillings' },
    { key: 'extraction', label: 'Extraction', patients: 14, value: '$16,600', campaignDescription: 'Extractions' },
  ],
};

/* ---- the agent page ------------------------------------------------------ */

// $41,200 + $38,900 + $16,300 = $96,400.
export const TC_AGENT_SEGMENTS: TcAgentSegment[] = [
  { label: 'Booked on their plan page', value: '$41,200', amount: 41200 },
  { label: 'Booked after a call', value: '$38,900', amount: 38900 },
  { label: 'Phoned in or at the desk', value: '$16,300', amount: 16300 },
];

export const TC_TODAY = [
  { value: '9', label: 'to call' },
  { value: '38', label: 'messages sent' },
  { value: '3', label: 'booked' },
  { value: '$4,120', label: 'of treatment' },
];

export const TC_RECENT_BOOKINGS: TcRecentBooking[] = [
  // Each row opens its own patient (fix 29 §2), on the Treatments tab.
  { id: 'rb1', patientId: 'amara-reza', patient: 'Rachel Moore', treatment: 'Crown #30', campaign: 'Freshly diagnosed', how: 'On their plan page', value: '$1,420' },
  { id: 'rb2', patientId: 'james-chen', patient: 'Marco Bianchi', treatment: 'Implant #19', campaign: 'Larger cases', how: 'After a call · Ava', value: '$4,300' },
  { id: 'rb3', patientId: 'sara-malik', patient: 'Priya Raman', treatment: 'Deep cleaning', campaign: 'Benefits ending', how: 'On their plan page', value: '$560' },
  { id: 'rb4', patientId: 'tom-rivera', patient: 'Tom Hughes', treatment: 'Root canal #3', campaign: 'Freshly diagnosed', how: 'Phoned in', value: '$1,180' },
  { id: 'rb5', patientId: 'james-chen', patient: 'Lena Park', treatment: 'Crown #14', campaign: 'Second try', how: 'After a call · Jordan', value: '$1,380' },
];

/** The needs-a-person banner (§9; amendment 34 §7). One to call, one to answer. */
export function tcTriageBanner(): { toCall: number; toAnswer: number; text: string } {
  // A call-me tap goes to Calls; a question on the plan goes to Triage.
  return { toCall: 1, toAnswer: 1, text: 'Maria Lopez asked to be called (Calls) · Sam Okoro asked a question (Triage)' };
}

/* ---- the plan page ------------------------------------------------------- */

export const PLAN_PAGE: PlanPage = {
  // 22-character code (Brain PRD v0.5 §30): {practice}.elva.ai/p/{code}.
  code: 'K7q9Xm2vR4tBw8LpZ3nQ5d',
  firstName: 'Todd',
  dob: '03/14/1974',
  diagnosedBy: 'Dr. Carter',
  diagnosedOn: '2 Sep',
  practiceName: 'Alamo Heights Dental',
  practiceInitials: 'AH',
  practicePhone: '(210) 555-0140',
  practiceColor: '#1D6F6A',
  language: 'en',
  expiresOn: '13 Oct',
  // Four chart lines (crown + build-up on #14; two scalings) grouped into two
  // cards by the §1.1 rules. No code or tooth number in any patient-facing field.
  procedures: [
    {
      n: 1,
      title: 'A crown — upper left back tooth',
      includes: 'Includes the build-up under the crown',
      staffName: 'Crown, #14 (with build-up)',
      why: 'The large old filling in this tooth has cracked. A crown covers the tooth and holds it together.',
      ifYouWait: 'The crack can grow and the tooth can break. That can mean a root canal, or losing the tooth.',
      costName: 'Crown',
      costSub: 'with the build-up',
      fee: '$1,850',
      insurance: '$925',
      youPay: '$925',
      chart: { arch: 'upper', teeth: [14] },
      xrayTint: '#E8EEF0',
      xrayCaption: 'Your x-ray from 2 Sep',
      source: 'elva',
      sourceDate: '2 Sep',
    },
    {
      n: 2,
      title: 'Deep cleaning — lower teeth',
      staffName: 'Deep cleaning, lower (scaling ×2)',
      why: 'Early gum pockets were found around these teeth at your cleaning. This clears them out.',
      ifYouWait: 'Gums can pull back and teeth can loosen over time.',
      costName: 'Deep cleaning',
      costSub: 'lower teeth',
      fee: '$280',
      insurance: '$224',
      youPay: '$56',
      // Charted on areas (lower right + lower left), not exact teeth: the whole lower
      // row is shaded, not filled (spec 48 rev 2 §1.2 / §1.4).
      chart: { arch: 'lower', teeth: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16], area: true },
      xrayTint: '#ECF0EA',
      xrayCaption: 'Your x-ray from 2 Sep',
      source: 'edited',
      sourceBy: 'Dana',
      sourceDate: '3 Oct',
    },
  ],
  totalFee: '$2,130',
  totalInsurance: '$1,149',
  totalYouPay: '$981',
  benefitsLeft: '$1,240',
  benefitsReset: '1 January',
  insuranceCarrier: 'Sun Life',
  insuranceCheckedOn: '21 Sep',
  bookLengthMins: 90,
  bookProvider: 'Dr. Carter',
};

/* ---- the campaign builder (§5) ------------------------------------------- */

import { tk } from '../msg/tokens';
import type { MsgField, MsgToken } from '../msg/tokens';
import type { TcAudiencePatient, TcCampaignDraft, TcFieldKey, TcStep } from './types';

/** The field menu (§5.2). No price, pay or x-ray fields — those live only on the plan page. */
export const TC_FIELDS: MsgField<TcFieldKey>[] = [
  { key: 'first_name', label: 'First name', example: 'Marguerite', mayBeEmpty: true, defaultFallback: 'there' },
  { key: 'treatment', label: 'Treatment', example: 'a crown', mayBeEmpty: false },
  { key: 'tooth', label: 'Tooth', example: 'your upper right back tooth', mayBeEmpty: true, defaultFallback: 'a tooth' },
  { key: 'dentist', label: 'Dentist', example: 'Dr. Saito', mayBeEmpty: false },
  { key: 'diagnosed', label: 'Diagnosed', example: 'on 18 Sep', mayBeEmpty: false },
  { key: 'benefits_left', label: 'Benefits left', example: '$1,100', mayBeEmpty: true, defaultFallback: 'some' },
  { key: 'benefits_reset', label: 'Benefits reset', example: '1 January', mayBeEmpty: false },
  { key: 'plan_link', label: 'Plan link', example: 'their private link', mayBeEmpty: false },
  { key: 'practice_name', label: 'Practice name', example: 'Alamo Heights Dental', mayBeEmpty: false },
  { key: 'practice_phone', label: 'Practice phone', example: '(210) 555-0147', mayBeEmpty: false },
];

const TC_KEYS: ReadonlySet<string> = new Set(TC_FIELDS.map((f) => f.key));
const t = (...parts: string[]): MsgToken<TcFieldKey>[] => tk(TC_KEYS, ...parts);

/** The starting steps for a new TC campaign (§5.2). Text/email only — no calls. */
export function tcStartingSteps(): TcStep[] {
  return [
    {
      id: 's1',
      channel: 'text',
      day: 0,
      mode: 'template',
      body: t(
        'Hi ',
        'first_name',
        ', ',
        'dentist',
        ' put together your plan for ',
        'treatment',
        ' ',
        'diagnosed',
        '. See what it involves and what your insurance covers here: ',
        'plan_link',
      ),
    },
    {
      id: 's2',
      channel: 'email',
      day: 3,
      mode: 'smart',
      subject: t('Your ', 'treatment', ', explained'),
      body: t('What ', 'treatment', ' involves, and what your insurance covers: ', 'plan_link'),
    },
    {
      id: 's3',
      channel: 'text',
      day: 10,
      mode: 'template',
      body: t('Hi ', 'first_name', ', any questions about your plan? ', 'plan_link'),
    },
    {
      id: 's4',
      channel: 'text',
      day: 30,
      mode: 'smart',
      body: t('One last note — your benefits reset ', 'benefits_reset', '. Your plan: ', 'plan_link'),
    },
  ];
}

/** A blank draft for /campaigns/new. */
export function blankTcDraft(description = ''): TcCampaignDraft {
  return { id: `tc-${String(Date.now())}`, name: '', description, steps: tcStartingSteps() };
}

/** The audience mock (§5.1, board TC3) — the patients a description resolves to. */
export const TC_AUDIENCE: TcAudiencePatient[] = [
  { id: 'p1', name: 'Marguerite Oyelowo', age: 54, insurance: 'Delta Dental', language: 'en', treatment: 'Crown #3 · Dr. Saito', treatmentShort: 'a crown', caseValue: '$1,380', patientPays: 'about $690', diagnosed: '18 Sep', status: 'will-get-it', statusLabel: 'Will get it' },
  { id: 'p2', name: 'Devon Achebe', age: 31, insurance: 'Cigna', language: 'en', treatment: 'Crown #30 · onlay #31 · Dr. Saito', treatmentShort: 'a crown and an onlay', caseValue: '$1,960', patientPays: 'about $1,050', diagnosed: '2 Sep', status: 'will-get-it', statusLabel: 'Will get it' },
  { id: 'p3', name: 'Ruth Sandoval', age: 67, insurance: 'no insurance', language: 'es', treatment: 'Crown #19 · Dr. Saito', treatmentShort: 'a crown', caseValue: '$1,240', patientPays: '$1,240', diagnosed: '12 Aug', status: 'will-get-it', statusLabel: 'Will get it' },
  { id: 'p4', name: 'Callum Brightwater', age: 28, insurance: 'MetLife', language: 'en', treatment: 'Onlay #14 · Dr. Saito', caseValue: '$1,090', patientPays: 'about $545', diagnosed: '22 Sep', status: 'in-campaign', statusLabel: 'In Freshly diagnosed' },
  { id: 'p5', name: 'Ines Vasconcelos', age: 45, insurance: 'Aetna', language: 'en', treatment: 'Crown #8 · Dr. Saito', caseValue: '$1,520', patientPays: 'about $760', diagnosed: '8 Jul', status: 'opted-out', statusLabel: 'Opted out' },
  { id: 'p6', name: 'Theo Lindqvist', age: 39, insurance: 'Delta Dental', language: 'en', treatment: 'Crown #12 · Dr. Saito', caseValue: '$1,410', patientPays: '$560 – $850', patientPaysStale: true, diagnosed: '21 Jun', status: 'will-get-it', statusLabel: 'Will get it' },
];

/** The readback the audience box shows (§5.1). */
export const TC_READBACK = {
  understood: [
    { label: 'Procedure type', value: 'crown or onlay' },
    { label: 'Case value', value: 'between $1,000 and $2,000' },
    { label: 'Diagnosed by', value: 'Dr. Saito' },
  ],
  leftOut: {
    phrase: 'who can afford it',
    reason:
      'Campaigns choose patients by the treatment — its value, what insurance covers — never by what a patient owes or earns. Remove it, or describe who you mean another way.',
  },
  alwaysApplied: [
    'The treatment is real',
    'Seen here in the last 18 months',
    'Nothing booked for it yet',
    'Can be contacted',
  ],
  match: 34,
  matchValue: '$49,300',
  willGet: 29,
  // The value of the 29 who will get it (not the 34 matched) — fix 29 §4.2.
  willGetValue: '$41,800',
  theOther:
    '3 are already in a campaign higher on the list (Freshly diagnosed, Larger cases) · 1 opted out of these messages · 1 has no mobile or email.',
};

/** The starting-point cards on the audience page (§5.1). */
export const TC_STARTING_POINTS = [
  { id: 'freshly-diagnosed', name: 'Freshly diagnosed', sub: 'Diagnosed in the last 30 days', patients: 61, description: 'Diagnosed in the last 30 days' },
  { id: 'benefits-ending', name: 'Benefits ending', sub: '$300+ left, resets within 3 months', patients: 84, description: '$300+ of benefits left, resets within 3 months' },
  { id: 'larger-cases', name: 'Larger cases', sub: '$1,500 or more, last 6 months', patients: 38, description: 'Cases of $1,500 or more, diagnosed in the last 6 months' },
  { id: 'second-try', name: 'Second try', sub: 'Contacted 90+ days ago, no reply', patients: 102, description: 'Contacted 90+ days ago, never replied' },
];

/**
 * Per-starting-point readback + counts (staging fix 29 §3.1 / §4.1). Each card
 * resolves it back differently, so the audience page never shows the Crowns
 * result for every description. The always-applied rules each carry a short
 * grey explanation, as the board.
 */
export const TC_ALWAYS_APPLIED: { label: string; sub: string }[] = [
  { label: 'The treatment is real', sub: 'In the chart, not done, not replaced, and the chart synced in the last day.' },
  { label: 'Seen here in the last 18 months', sub: 'Patients away longer are Recall’s reactivation.' },
  { label: 'Nothing booked for it yet', sub: 'A booked treatment leaves the campaign on its own.' },
  { label: 'Can be contacted', sub: 'Has a mobile or email, didn’t opt out, not over the message limit.' },
];

interface TcReadback {
  understood: { label: string; value: string }[];
  match: number;
  matchValue: string;
  willGet: number;
  theOther: string;
}

const TC_READBACK_BY_START: Record<string, TcReadback> = {
  'freshly-diagnosed': {
    understood: [
      { label: 'Diagnosed', value: 'in the last 30 days' },
    ],
    match: 61, matchValue: '$88,900', willGet: 61,
    theOther: 'Everyone freshly diagnosed can get this — it’s the top of the list.',
  },
  'benefits-ending': {
    understood: [
      { label: 'Benefits left', value: '$300 or more' },
      { label: 'Benefit year', value: 'resets within 3 months' },
    ],
    match: 84, matchValue: '$61,200', willGet: 79,
    theOther: '3 are already in a campaign higher on the list · 2 have no mobile or email.',
  },
  'larger-cases': {
    understood: [
      { label: 'Case value', value: '$1,500 or more' },
      { label: 'Diagnosed', value: 'in the last 6 months' },
    ],
    match: 38, matchValue: '$74,600', willGet: 34,
    theOther: '3 are already in a campaign higher on the list · 1 opted out of these messages.',
  },
  'second-try': {
    understood: [
      { label: 'Contacted', value: '90 or more days ago' },
      { label: 'Reply', value: 'never replied' },
    ],
    match: 102, matchValue: '$120,400', willGet: 91,
    theOther: '8 are already in a campaign higher on the list · 3 opted out of these messages.',
  },
};

/** The default (typed / Opportunity) readback — the Crowns result. */
const TC_READBACK_DEFAULT: TcReadback = {
  understood: TC_READBACK.understood,
  match: TC_READBACK.match,
  matchValue: TC_READBACK.matchValue,
  willGet: TC_READBACK.willGet,
  theOther: TC_READBACK.theOther,
};

/** Resolve the readback for a description — a starting point's own, else Crowns. */
export function tcReadbackFor(description: string): TcReadback {
  const sp = TC_STARTING_POINTS.find((s) => s.description === description);
  return (sp !== undefined ? TC_READBACK_BY_START[sp.id] : undefined) ?? TC_READBACK_DEFAULT;
}

/* ---- the cheat-sheet right rail (§7) ------------------------------------- */

export interface TcCheatSignal { label: string; when: string; strongest?: boolean }
export interface TcCheatEvent {
  at: string;
  kind: string;
  body?: string;
  /** The original, in the patient's language, shown RTL when "showing original" (fix 29 §5). */
  bodyFa?: string;
}
export interface TcCheatSheet {
  patientId: string;
  planValue: string;
  planValueSub: string;
  hePays: string;
  benefitsLeft: string;
  benefitsResetIn: string;
  plan: {
    status: 'active' | 'locked' | 'expired' | 'none';
    link: string;
    opened: string;
    viewedXray: string;
    worksUntil: string;
  };
  whyCallNow: TcCheatSignal[];
  campaignNote: string;
  history: TcCheatEvent[];
  language: LanguageCode;
}

/** Todd Baird — the cheat-sheet mock (§7). One name, he/him throughout. */
export const TC_CHEAT: TcCheatSheet = {
  patientId: 'todd-baird',
  planValue: '$2,130',
  planValueSub: '2 procedures',
  hePays: '~$981',
  benefitsLeft: '$1,240',
  benefitsResetIn: '94 days',
  plan: {
    status: 'active',
    link: 'alamoheights.elva.ai/p/K7q9Xm2vR4tBw8LpZ3nQ5d',
    opened: 'Opened 3 times · last yesterday 7:42 pm',
    viewedXray: 'Viewed the x-ray · yesterday',
    worksUntil: 'Works until 13 Oct — the next message renews it',
  },
  whyCallNow: [
    { label: 'Replied about his plan', when: 'yesterday 7:48 pm', strongest: true },
    { label: 'Viewed his x-ray', when: 'yesterday 7:44 pm' },
    { label: 'Opened his plan 3 times', when: '3 Sep · 27 Sep · 28 Sep' },
  ],
  campaignNote:
    'Freshly diagnosed · 3 of 4 messages sent. Paused for Todd until the end of Wed 30 Sep, then it carries on if nobody calls.',
  history: [
    { at: '28 Sep 7:48 pm', kind: 'Todd replied', body: 'Is the crown covered now? Last time it was too much. Can I do it in January instead?', bodyFa: 'آیا روکش الان تحت پوشش بیمه است؟ دفعه قبل خیلی زیاد بود. می‌توانم آن را در ژانویه انجام دهم؟' },
    { at: '28 Sep 7:42 pm', kind: 'Opened his plan · viewed the x-ray' },
    { at: '27 Sep 9:20 pm', kind: 'Opened his plan' },
    { at: '13 Sep 10:00 am', kind: 'Text · step 3', body: 'Hi Todd, any questions about your plan from Dr. Carter? Everything is here: elva.ai/p/K7q9…', bodyFa: 'سلام تاد، درباره طرح درمان دکتر کارتر سؤالی دارید؟ همه چیز اینجاست: elva.ai/p/K7q9…' },
    { at: '6 Sep 9:30 am', kind: 'Email · step 2', body: 'Your crown, explained. Dr. Carter found a crack in the old filling on your upper left back tooth…', bodyFa: 'روکش شما، توضیح داده شده. دکتر کارتر ترکی در پرکردگی قدیمی دندان عقب بالا سمت چپ شما پیدا کرد…' },
    { at: '3 Sep 6:12 pm', kind: 'Opened his plan' },
    { at: '3 Sep 10:00 am', kind: 'Text · step 1', body: 'Hi Todd, Dr. Carter put together your plan for a crown and a deep cleaning on 2 Sep. See what it involves and what your insurance covers here: elva.ai/p/K7q9…', bodyFa: 'سلام تاد، دکتر کارتر طرح شما را برای یک روکش و جرم‌گیری عمقی در ۲ سپتامبر آماده کرد. ببینید شامل چه می‌شود و بیمه چه چیزی را پوشش می‌دهد: elva.ai/p/K7q9…' },
  ],
  language: 'fa',
};
